import { useState } from 'react'
import { useDeleteTeam, useSaveTeam, useTeamMembers, useTeams, useUsers } from '../../api/queries'
import type { Team } from '../../api/types'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../../components/ui'

/**
 * Teams: de faste hold, en sag kan sættes på (docs/DECISIONS.md beslutning 068).
 *
 * <b>Et team er et hold, ikke en adgang.</b> Det styrer ikke, hvem der må se
 * hvad - det siger, hvem der plejer at arbejde sammen, så en sag kan sættes på
 * "Tavlefolkene" frem for på fem navne. Rettighederne kommer stadig fra rollen.
 *
 * Sagen kunne vælge et team fra dag ét, men der var ingen måde at oprette dem -
 * så listen var tom, og feltet uden virkning.
 */
export function TeamsPage() {
  const { data, isLoading, error } = useTeams()
  const { data: users } = useUsers()

  const remove = useDeleteTeam()

  const [editing, setEditing] = useState<Team | 'new' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  async function drop(team: Team) {
    const advarsel = team.projectCount > 0
      ? `Fjern teamet "${team.name}"? De ${team.projectCount} ${team.projectCount === 1 ? 'sag' : 'sager'} på det bliver stående - de kommer bare til at stå uden team.`
      : `Fjern teamet "${team.name}"?`

    if (!window.confirm(advarsel)) return

    setProblem(null)
    try {
      await remove.mutateAsync(team.id)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Teamet kunne ikke fjernes.')
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  if (editing) {
    return (
      <TeamForm
        team={editing === 'new' ? null : editing}
        users={users ?? []}
        onClose={() => setEditing(null)}
      />
    )
  }

  const teams = data ?? []

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Teams
        </h1>
        <button className="btn btn--primary" onClick={() => setEditing('new')}>
          Nyt team
        </button>
      </div>

      <Card>
        <p className="small muted" style={{ marginTop: 0 }}>
          Et team er et hold, en sag kan sættes på - "Tavlefolkene" frem for fem navne.{' '}
          <strong>Det styrer ikke, hvem der må se hvad</strong>; rettighederne kommer fra rollen.
          Fjerner du et team, bliver sagerne stående uden team.
        </p>

        {problem && <div className="error-box" style={{ marginBottom: 12 }}>{problem}</div>}

        {teams.length === 0 && (
          <EmptyState>
            Der er ingen teams endnu. Opret det første, så kan sagerne sættes på det.
          </EmptyState>
        )}

        <div className="stack" style={{ gap: 2 }}>
          {teams.map((team) => (
            <div
              key={team.id}
              className="row row--between device-row"
              style={{ gap: 10, opacity: team.isActive ? 1 : 0.55 }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>
                  {team.name}
                  {!team.isActive && <span className="tiny muted"> · ikke i brug</span>}
                </div>
                {team.description && <div className="small muted">{team.description}</div>}
                <div className="tiny muted">
                  {team.memberCount === 0
                    ? 'Ingen på holdet'
                    : `${team.memberCount} ${team.memberCount === 1 ? 'mand' : 'mand'} på holdet`}
                  {team.projectCount > 0 &&
                    ` · ${team.projectCount} ${team.projectCount === 1 ? 'sag' : 'sager'}`}
                </div>
                {/* Profilbillederne af holdet (beslutning 142). */}
                {team.members.length > 0 && (
                  <div className="row" style={{ gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {team.members.map((member) => (
                      <Avatar key={member.id} user={member} size={30} title={member.displayName} />
                    ))}
                  </div>
                )}
              </div>

              <div className="btn-group">
                <button className="btn btn--sm" onClick={() => setEditing(team)}>
                  Rediger
                </button>
                <button className="btn btn--sm" onClick={() => void drop(team)}>
                  Fjern
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

/**
 * Opret eller ret et team, med holdet på samme side.
 *
 * Medlemmerne sendes med i selve gemningen (`memberIds`), så et team og dets
 * hold aldrig kan nå at være ude af trit - listen ER svaret på "hvem er med".
 */
function TeamForm({
  team,
  users,
  onClose
}: {
  team: Team | null
  users: { id: number; displayName: string; email: string; initials?: string | null; photoUrl?: string | null }[]
  onClose: () => void
}) {
  const save = useSaveTeam()
  const { data: members, isLoading } = useTeamMembers(team?.id ?? null)

  const [name, setName] = useState(team?.name ?? '')
  const [description, setDescription] = useState(team?.description ?? '')
  const [isActive, setIsActive] = useState(team?.isActive ?? true)
  const [picked, setPicked] = useState<number[] | null>(team ? null : [])
  const [problem, setProblem] = useState<string | null>(null)

  // Medlemmerne hentes for sig. Foerste gang de er der, fyldes listen.
  if (team && picked === null) {
    if (isLoading || !members) return <Loading />
    setPicked(members.map((m) => m.id))
    return <Loading />
  }

  const chosen = picked ?? []

  function toggle(userId: number) {
    setPicked(chosen.includes(userId) ? chosen.filter((id) => id !== userId) : [...chosen, userId])
  }

  async function submit() {
    if (!name.trim()) {
      setProblem('Teamet skal have et navn.')
      return
    }

    setProblem(null)
    try {
      await save.mutateAsync({
        id: team?.id,
        input: {
          name: name.trim(),
          description: description.trim() || null,
          isActive,
          memberIds: chosen
        }
      })
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Teamet kunne ikke gemmes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          {team ? `Rediger ${team.name}` : 'Nyt team'}
        </h1>
        <div className="btn-group">
          <button className="btn" onClick={onClose}>
            Fortryd
          </button>
          <button className="btn btn--primary" disabled={save.isPending} onClick={() => void submit()}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
        </div>
      </div>

      {problem && <div className="error-box">{problem}</div>}

      <Card title="Om teamet">
        <div className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Navn</span>
            <input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Fx Tavlefolkene"
            />
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Beskrivelse</span>
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Hvad laver holdet?"
            />
          </label>

          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            <span className="small">
              I brug
              <span className="tiny muted"> - et team der ikke er i brug, kan ikke vælges på nye sager</span>
            </span>
          </label>
        </div>
      </Card>

      <Card title={`Holdet (${chosen.length})`}>
        <p className="small muted" style={{ marginTop: 0 }}>
          Sæt flueben ved dem, der er med. Listen erstatter holdet, som det ser ud nu.
        </p>

        <div className="stack" style={{ gap: 2 }}>
          {users.map((user) => (
            <label
              key={user.id}
              className="row device-row"
              style={{ gap: 10, alignItems: 'center', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={chosen.includes(user.id)}
                onChange={() => toggle(user.id)}
              />
              <Avatar user={user} size={28} />
              <div style={{ minWidth: 0 }}>
                <div className="small">{user.displayName}</div>
                <div className="tiny muted">{user.email}</div>
              </div>
            </label>
          ))}
        </div>

        {users.length === 0 && <EmptyState>Der er ingen brugere at vælge.</EmptyState>}
      </Card>
    </div>
  )
}
